/**
 * Normalise a phone number to E.164. WhatsApp hands us digits with no `+`
 * (`233201234567`), humans type `020 123 4567`, so both must land on the
 * same customer record.
 *
 * `defaultCc` only applies to trunk-format numbers beginning `0`, which are
 * ambiguous by nature: `0788123456` is Rwandan in Kigali and Ghanaian in
 * Accra. Callers that know the branch should pass its code — the default
 * is a fallback, not an assumption worth relying on in a second country.
 *
 * @param input      Raw number in any of the shapes above.
 * @param defaultCc  Country calling code for local-format numbers.
 */
export function toE164(input, defaultCc = '233') {
    const digits = input.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) {
        return /^\+\d{8,15}$/.test(digits) ? digits : null;
    }
    // Local trunk format: 0201234567 → +233201234567
    if (digits.startsWith('0')) {
        return `+${defaultCc}${digits.slice(1)}`;
    }
    // Already country-prefixed but missing the plus, as WhatsApp sends it.
    if (digits.length >= 8) {
        return `+${digits}`;
    }
    return null;
}
/** `+233201234567` → `+233 20 123 4567`, for display only. */
export function formatPhone(e164) {
    const match = /^\+(\d{1,3})(\d{2})(\d{3})(\d{4})$/.exec(e164);
    if (!match)
        return e164;
    return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
}
/**
 * Dialling code to ISO-3166 alpha-2, for the countries Zuruflex operates in
 * or borders.
 *
 * Deliberately not exhaustive: this decides which branch's fleet a
 * customer is shown, so an unknown code must return null and let the
 * caller ask rather than guess a country and quote in the wrong currency.
 */
const DIALLING_CODE_COUNTRIES = {
    '250': 'RW', // Rwanda
    '233': 'GH', // Ghana
    '254': 'KE', // Kenya
    '256': 'UG', // Uganda
    '255': 'TZ', // Tanzania
    '257': 'BI', // Burundi
    '243': 'CD', // DR Congo
    '234': 'NG', // Nigeria
    '27': 'ZA', // South Africa
};
/**
 * The country a number belongs to, or null when we cannot tell.
 *
 * @param e164 A number in E.164, with or without the leading `+`.
 */
export function countryFromPhone(e164) {
    const digits = e164.replace(/^\+/, '').replace(/\D/g, '');
    // Longest prefix wins, so 250 is not shadowed by 25.
    for (const length of [3, 2, 1]) {
        const match = DIALLING_CODE_COUNTRIES[digits.slice(0, length)];
        if (match)
            return match;
    }
    return null;
}
/**
 * The dialling code for a country, without the `+`: `'RW'` → `'250'`.
 *
 * What `toE164` should be handed whenever the caller knows the country —
 * the partner's, the branch's — so a Kigali number typed `078…` is not
 * read as Ghanaian. Null for a country outside the table, which leaves the
 * caller to fall back to its configured default.
 */
export function diallingCodeFor(country) {
    if (!country)
        return null;
    const upper = country.toUpperCase();
    for (const [code, iso] of Object.entries(DIALLING_CODE_COUNTRIES)) {
        if (iso === upper)
            return code;
    }
    return null;
}
/**
 * Digits after the country code, for the countries whose plan we hold a
 * local number to. Anywhere else, `phoneIn` takes the number on trust.
 */
const NATIONAL_DIGITS = { GH: 9, RW: 9 };
/** How a local number looks, for a placeholder: `'RW'` → `'078 123 4567'`. */
const LOCAL_EXAMPLES = {
    GH: '024 123 4567',
    RW: '078 123 4567',
};
export function localPhoneExample(country) {
    return country ? LOCAL_EXAMPLES[country.toUpperCase()] : undefined;
}
/**
 * A number typed by somebody in `country`, in E.164 — or null when it is
 * not one.
 *
 * A number written with `+` is taken as written, so a foreign line can
 * still be given. A local one is read against the country's own code and,
 * where we know the plan, held to its length: `toE164` alone would turn a
 * half-typed `0241` into a "valid" `+233241`.
 *
 * @param country ISO-3166 alpha-2; unknown or null falls back to `toE164`'s
 *                own default.
 */
export function phoneIn(raw, country) {
    const code = diallingCodeFor(country);
    if (!code)
        return toE164(raw);
    const e164 = toE164(raw, code);
    if (!e164)
        return null;
    const digits = NATIONAL_DIGITS[country.toUpperCase()];
    if (digits && e164.startsWith(`+${code}`) && e164.length - code.length - 1 !== digits) {
        return null;
    }
    return e164;
}
